/**
 * sessionStorage key holding the 6-digit random suffix of `customerNumber`.
 * Session-scoped on purpose: the suffix identifies one visit, so it has to
 * survive navigation between pages of the same tab but must not outlive it.
 */
export const FORM_CUSTOMER_ID_STORAGE_KEY = 'sh-form-customer-id-suffix';

/**
 * Last resort when sessionStorage is unavailable (Safari private mode, a
 * blocked-storage setting). Keeps every form of one page load in agreement,
 * which is as far as an in-memory copy can reach.
 */
let inMemoryRandomPart: string | undefined;

function createRandomPart(): string {
  return String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0');
}

/**
 * The 6-digit suffix, one per browser tab: generated the first time a form
 * asks for it and then read back from sessionStorage, so every form the
 * visitor meets during the visit — on any page — submits the same suffix.
 */
function sessionRandomPart(): string {
  if (typeof window === 'undefined') {
    // SSR. There is no tab to scope a suffix to, and a module-level cache
    // would leak one visitor's suffix into another's HTML. The client picks
    // the session's real suffix up again when the form mounts.
    return createRandomPart();
  }
  try {
    const stored = window.sessionStorage.getItem(FORM_CUSTOMER_ID_STORAGE_KEY);
    if (stored !== null && /^\d{6}$/.test(stored)) {
      return stored;
    }
    const fresh = createRandomPart();
    window.sessionStorage.setItem(FORM_CUSTOMER_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return (inMemoryRandomPart ??= createRandomPart());
  }
}

/**
 * Generates the value submitted as `customerNumber` on lead-capture forms
 * and rendered client-side as the hidden `.form-customerId` DOM field that
 * IntelliAd (a third-party marketing tag) reads via
 * `document.querySelector('.form-customerId').value`.
 *
 * Format: the location's `centerNumber` zero-padded to at least 4 digits
 * (or "0000" when there is no location), followed by a 6-digit random
 * number. Must stay compatible with the backend's validation
 * (`^\d{10,}$`, see `SubmitFormBaseDTO.customerNumber`).
 *
 * The two halves have different lifetimes. The center part follows the page
 * being rendered, so the same visitor submitting on two Standort pages
 * reports two different centers. The random part identifies the visit and is
 * stable for the lifetime of the tab (see `FORM_CUSTOMER_ID_STORAGE_KEY`),
 * so every form of that visit shares one suffix instead of each mount
 * inventing its own.
 */
export function generateFormCustomerId(
  centerNumber: number | undefined,
): string {
  const centerPart = String(centerNumber ?? 0).padStart(4, '0');
  return `${centerPart}${sessionRandomPart()}`;
}
